/**
 * Team View — projects + people, scoped to one team.
 *
 * Two tensions resolved per Brett's call:
 *   - Both views: "Projects we own" + "What our people are loaded with"
 *   - Mini-bar timeline (cheaper than a full Gantt, more visual than dates)
 *   - What-if: skip for v1, link to Planning instead
 *
 * v1 scope is Synnergie team only (no team picker yet) — see if the shape
 * works before generalizing.
 */
import { Fragment, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, Users, Briefcase, ArrowRight, ChevronDown, ChevronRight } from "lucide-react";
import { TopBar } from "@/components/layout/TopBar";
import { useRoster, usePersonDemand, useRosterAssignments } from "@/hooks/useRoster";
import { usePortfolio } from "@/hooks/usePortfolio";
import { useCapacityTiers } from "@/hooks/useCapacityRebuild";
import { countsForCapacity } from "@/lib/capacityScope";
import { useAllMilestones } from "@/hooks/useMilestones";
import { cn } from "@/lib/cn";
import { avatarTone, formatDate } from "@/lib/format";
import { computeAvailability, projPhaseAt } from "@/lib/teamAvailability";
import { statusShortLabel, statusPillClass, effectiveHealth } from "@/lib/projectStatus";
import { GanttChart } from "@/components/timeline/GanttChart";
import { CellMathModal } from "@/components/team/CellMathModal";

/** Team-page registry. Each entry maps a URL slug → the actual `team`
 *  value(s) stored on roster + project rows + the user-facing label
 *  that shows in the page header.
 *
 *  `dataKeys` is an array because a "team page" can pool multiple
 *  underlying teams. Single-team pages have a one-element array; the
 *  combined "All Teams" view passes both. Filtering is case-insensitive
 *  member.team ∈ dataKeys.
 *
 *  `breakdown` (optional) is the per-team list rendered on combined
 *  pages — each entry produces a small card showing that single team's
 *  3-up. Skip this on single-team pages. */
const TEAM_REGISTRY: Record<
  string,
  {
    dataKeys: string[];
    displayName: string;
    breakdown?: Array<{ dataKey: string; displayName: string; slug: string }>;
  }
> = {
  synnergie: {
    dataKeys: ["Synnergie"],
    displayName: "Synnergie",
  },
  "applications-development": {
    // Slug stays as-is so existing bookmarks survive; displayName is
    // the canonical roster team name ("Systems Applications") so the
    // page header, sidebar entry, and heatmap subgroup label all read
    // the same. Brett caught the inconsistency 2026-06-04.
    dataKeys: ["Systems Applications"],
    displayName: "Systems Applications",
  },
  "business-analysts": {
    dataKeys: ["Business Analysts"],
    displayName: "Business Analysts",
  },
  all: {
    dataKeys: ["Synnergie", "Systems Applications", "Business Analysts"],
    displayName: "All Teams (IT Delivery)",
    breakdown: [
      { dataKey: "Synnergie", displayName: "Synnergie", slug: "synnergie" },
      { dataKey: "Systems Applications", displayName: "Systems Applications", slug: "applications-development" },
      { dataKey: "Business Analysts", displayName: "Business Analysts", slug: "business-analysts" },
    ],
  },
};

const ROLE_LABEL: Record<string, string> = {
  pm: "Project Manager", ba: "Business Analyst", functional: "Functional",
  technical: "Technical", developer: "Developer", infrastructure: "Infrastructure",
  dba: "DBA", erp: "Synnergie Consultant",
};

function initials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return (name.slice(0, 2) || "??").toUpperCase();
}

/** Map utilization → tone. Same thresholds as the rest of the planner. */
function utilTone(util: number): "green" | "amber" | "red" | "slate" {
  if (util <= 0.001) return "slate";
  if (util >= 1.0) return "red";
  if (util >= 0.8) return "amber";
  return "green";
}

const TONE_CLASSES: Record<string, { bar: string; text: string; bg: string; label: string }> = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300", bg: "bg-emerald-50 dark:bg-emerald-500/10",  label: "On track" },
  amber: { bar: "bg-amber-500",   text: "text-amber-700 dark:text-amber-300",   bg: "bg-amber-50 dark:bg-amber-500/10",    label: "Stretched" },
  red:   { bar: "bg-red-500",     text: "text-red-700 dark:text-red-300",     bg: "bg-red-50 dark:bg-red-500/10",      label: "Over capacity" },
  slate: { bar: "bg-muted-strong",   text: "text-ink-muted",   bg: "bg-canvas",    label: "No demand yet" },
};

export function TeamView() {
  // Resolve which team this route is showing. URL slug → registry entry.
  // Falls back to Synnergie for the legacy/no-param case so the original
  // /teams/synnergie route keeps working without param wiring.
  const params = useParams<{ teamSlug?: string }>();
  const slug = params.teamSlug ?? "synnergie";
  const team = TEAM_REGISTRY[slug] ?? TEAM_REGISTRY.synnergie;
  const TEAM_NAME = team.displayName;
  // Set of underlying team data keys this page pools. Single-team pages
  // have one entry; the "all" page has multiple. Lowercased for
  // case-insensitive comparison against roster + project team fields.
  const TEAM_DATA_KEYS = useMemo(
    () => new Set(team.dataKeys.map((k) => k.toLowerCase())),
    [team.dataKeys],
  );
  const isCombined = !!team.breakdown;
  const rosterQ = useRoster();

  // Math-breakdown modal state — which cell is being explained.
  const [mathModalCell, setMathModalCell] = useState<
    | { projectId: string; roleKey: string; displayedHours: number }
    | null
  >(null);
  // Current-load dataset (in-flight only) — feeds the In-flight (avg) stat.
  const currentDemandQ = usePersonDemand(false);
  // Forecast dataset (current + approved + ready_for_development) — feeds
  // every other section on the page.
  const forecastDemandQ = usePersonDemand(true);
  // Single source for the drill-in sections (Load by Role, Math Breakdown).
  // Always shows the full forecast picture; per-row status pills already
  // tell the user which rows are in-flight vs approved/queued.
  const demandQ = forecastDemandQ;
  const portfolioQ = usePortfolio(false);
  const milestonesQ = useAllMilestones();

  // ---- People on this team ---------------------------------------------
  // Filter by the data key (the value stored on the roster row), NOT the
  // display name. e.g. "Applications Development" maps to "Systems
  // Applications" in the data.
  const teamMembers = useMemo(() => {
    const all = Array.isArray(rosterQ.data) ? rosterQ.data : [];
    return all.filter((m) => TEAM_DATA_KEYS.has((m.team ?? "").toLowerCase()));
  }, [rosterQ.data, TEAM_DATA_KEYS]);

  const teamMemberNames = useMemo(() => new Set(teamMembers.map((m) => m.name)), [teamMembers]);

  // Per-team-member demand rows. Filtered from whichever dataset the
  // toggle is currently on. Drives the per-person + role-grouped views.
  const teamDemand = useMemo(() => {
    const all = Array.isArray(demandQ.data) ? demandQ.data : [];
    return all.filter((d) => teamMemberNames.has(d.name));
  }, [demandQ.data, teamMemberNames]);

  // ---- Aggregate summary helper. Members with include_in_capacity=False
  //      are excluded from the math (they appear in the expanded list
  //      with an "excluded" pill but don't drag the % down).
  const summarize = (rows: typeof teamDemand) => {
    let supply = 0;
    let demand = 0;
    let excludedCount = 0;
    for (const d of rows) {
      if (!d.include_in_capacity) {
        excludedCount++;
        continue;
      }
      supply += d.capacity_hrs ?? 0;
      demand += d.total_weekly_hrs ?? 0;
    }
    const util = supply > 0 ? demand / supply : 0;
    return { supply, demand, util, tone: utilTone(util), excludedCount };
  };

  // Both summaries are computed so the headline can run all three
  // windowed-average stats (in-flight, forecast, if-today).
  const currentTeamDemand = useMemo(() => {
    const all = Array.isArray(currentDemandQ.data) ? currentDemandQ.data : [];
    return all.filter((d) => teamMemberNames.has(d.name));
  }, [currentDemandQ.data, teamMemberNames]);
  const forecastTeamDemand = useMemo(() => {
    const all = Array.isArray(forecastDemandQ.data) ? forecastDemandQ.data : [];
    return all.filter((d) => teamMemberNames.has(d.name));
  }, [forecastDemandQ.data, teamMemberNames]);
  const currentSummary = useMemo(() => summarize(currentTeamDemand), [currentTeamDemand]);
  const forecastSummary = useMemo(() => summarize(forecastTeamDemand), [forecastTeamDemand]);

  // ---- Per-person assignments + availability --------------------------
  // CFO ask: for each member, which projects they're assigned to (with
  // dates) so we can see when they free up for the next project. Joins the
  // per-person ASSIGNED demand entries with the portfolio for dates.
  const [expandedPeople, setExpandedPeople] = useState<Set<string>>(new Set());
  const togglePerson = (name: string) =>
    setExpandedPeople((s) => {
      const next = new Set(s);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  const assignQ = useRosterAssignments();

  type PersonProject = { id: string; name: string; start: string | null; end: string | null; hours: number };
  // Per-person live-or-upcoming assignments, straight from the assignment
  // table via /roster/assignments — the raw hours_per_week on each project,
  // NOT the capacity engine's demand (which dropped ready-for-dev work and
  // phase-adjusted hours: the Ajay bug).
  // Capacity scope (2026-10-06): only bands chosen on Admin count.
  const capacityTiers = useCapacityTiers();
  const scopedProjectIds = useMemo(() => {
    const rows = Array.isArray(portfolioQ.data) ? portfolioQ.data : [];
    return new Set(rows.filter((p) => countsForCapacity(p, capacityTiers)).map((p) => p.id));
  }, [portfolioQ.data, capacityTiers]);
  const assignmentsByPerson = useMemo(() => {
    const out = new Map<string, PersonProject[]>();
    for (const pa of assignQ.data ?? []) {
      const list: PersonProject[] = (pa.assignments ?? [])
        .filter((a) => scopedProjectIds.has(a.project_id))
        .map((a) => ({
          id: a.project_id,
          name: a.project_name ?? a.project_id,
          start: a.start_date,
          end: a.end_date,
          hours: a.hours_per_week ?? 0,
        }))
        .sort((a, b) => {
          const ea = a.end ? new Date(a.end).getTime() : Infinity;
          const eb = b.end ? new Date(b.end).getTime() : Infinity;
          return ea - eb;
        });
      out.set(pa.name, list);
    }
    return out;
  }, [assignQ.data, scopedProjectIds]);

  // A project consumes a person's capacity while TODAY is inside its
  // [start, end] window — the same date-driven model the heatmap uses
  // (status is irrelevant; no phase math). Matches the heatmap exactly.
  const todayMs = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); }, []);
  const projPhase = (p: { start: string | null; end: string | null }) => projPhaseAt(p, todayMs);

  // Project-available capacity per person = weekly hrs × (1 − support
  // reserve). This is the honest denominator: the support carve-out is NOT
  // available for project work, so allocation % is measured against it.
  const capacityByPerson = useMemo(() => {
    const m = new Map<string, number>();
    for (const tm of teamMembers) {
      m.set(tm.name, (tm.weekly_hrs_available ?? 0) * (1 - (tm.support_reserve_pct ?? 0)));
    }
    return m;
  }, [teamMembers]);

  type AvailInfo = {
    spareHrs: number; assignedHrs: number; capacity: number; utilPct: number;
    tone: "green" | "amber" | "red" | "slate"; clears: string | null; hasOpenEnded: boolean;
  };
  const availabilityByPerson = useMemo(() => {
    const out = new Map<string, AvailInfo>();
    for (const [name, list] of assignmentsByPerson) {
      // Date-window load math (heatmap-equivalent), unit-tested in
      // lib/teamAvailability. Tone is the only UI concern added here.
      const a = computeAvailability(list, capacityByPerson.get(name) ?? 0, todayMs);
      const hasCommitments = list.some((p) => !projPhase(p).ended);
      const tone: AvailInfo["tone"] =
        !hasCommitments ? "green" : a.utilPct >= 1.0 ? "red" : a.utilPct >= 0.8 ? "amber" : "green";
      out.set(name, { ...a, tone });
    }
    return out;
  }, [assignmentsByPerson, capacityByPerson, todayMs]);


  // ---- Projects we own ------------------------------------------------
  const ownedProjects = useMemo(() => {
    const all = Array.isArray(portfolioQ.data) ? portfolioQ.data : [];
    const matchTeam = (p: { team: string | null }) =>
      TEAM_DATA_KEYS.has((p.team ?? "").toLowerCase());
    // Commitment scope only: in-flight (functional_spec → testing_go_live)
    // + approved + ready_for_development. Excludes:
    //   complete / declined / postponed → not actionable
    //   requested                       → intake-stage, no spec yet, dates
    //                                     and estimates are placeholders.
    //                                     Including them would pollute
    //                                     capacity math with phantom demand
    //                                     for work that may never happen.
    //                                     Pipeline lives in /helpdesk,
    //                                     not here.
    const inScope = (p: { status: string | null }) => {
      const s = (p.status ?? "").toLowerCase();
      return (
        s === "functional_spec" ||
        s === "technical_spec" ||
        s === "in_progress" ||
        s === "testing_go_live" ||
        s === "approved" ||
        s === "ready_for_development"
      );
    };
    return all
      .filter((p) => matchTeam(p) && inScope(p))
      .sort((a, b) => {
        // Active first (in_progress, testing), then in-spec, then approved.
        // Within each band: by start date asc (earliest first).
        const order: Record<string, number> = {
          in_progress: 0, testing_go_live: 1, technical_spec: 2,
          functional_spec: 3, ready_for_development: 4, approved: 5,
        };
        const oa = order[a.status ?? ""] ?? 99;
        const ob = order[b.status ?? ""] ?? 99;
        if (oa !== ob) return oa - ob;
        const sa = a.start_date ? new Date(a.start_date).getTime() : Infinity;
        const sb = b.start_date ? new Date(b.start_date).getTime() : Infinity;
        return sa - sb;
      });
  }, [portfolioQ.data, TEAM_DATA_KEYS]);

  // ---- Three windowed-average headline stats --------------------------
  // All three answer "what's the average team utilization across some
  // window of weeks?", differing only in (a) which projects define the
  // window and (b) whether dates are shifted. Restricted to projects
  // this team OWNS so cross-team assignments don't drag the math.
  //
  //   IN-FLIGHT     — window = min start → max end across current
  //                   in-flight owned projects. Demand source: the
  //                   engine's CURRENT-load run (currentTeamDemand).
  //                   Answers "across the runtime of what we're
  //                   actually working on, how loaded are we?"
  //
  //   FORECAST      — window = min start → max end across approved/
  //                   ready_for_dev owned projects. Demand source:
  //                   the engine's FORECAST run (forecastTeamDemand,
  //                   which already includes both current + approved).
  //                   Answers "when the queue runs, how loaded are we?"
  //                   Current projects active during the early weeks
  //                   of the window are correctly captured because the
  //                   engine includes them.
  //
  //   IF WE START   — same approved/ready_for_dev set, but each
  //   TODAY          project shifted so start_date = today (duration
  //                   preserved). Window = today → today + max
  //                   shifted duration. Current in-flight projects
  //                   stay on their actual dates and overlap. Answers
  //                   "if we couldn't wait for the planned start, would
  //                   the team survive starting now?"
  const ownedProjectMeta = useMemo(() => {
    const m = new Map<string, { status: string | null; start: Date | null; end: Date | null; durationDays: number | null }>();
    for (const p of ownedProjects) {
      const start = p.start_date ? new Date(p.start_date) : null;
      const end = p.end_date ? new Date(p.end_date) : null;
      const dur = start && end ? Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000)) : null;
      m.set(p.id, { status: p.status, start, end, durationDays: dur });
    }
    return m;
  }, [ownedProjects]);

  const isInFlightStatus = (s: string | null) => {
    const v = (s ?? "").toLowerCase();
    return v === "functional_spec" || v === "technical_spec" || v === "in_progress" || v === "testing_go_live";
  };
  const isApprovedQueueStatus = (s: string | null) => {
    const v = (s ?? "").toLowerCase();
    return v === "approved" || v === "ready_for_development";
  };

  const mondayOf = (d: Date) => {
    const r = new Date(d);
    r.setHours(0, 0, 0, 0);
    const day = r.getDay();
    r.setDate(r.getDate() - day + (day === 0 ? -6 : 1));
    return r;
  };

  /** Walk weeks in [windowStart..windowEnd], summing hours from active
   *  projects in each week. Returns mean util across weeks plus the
   *  count of distinct projects that contributed, so the sublabel can
   *  say "N projects active." */
  const computeWindowAvg = (
    windowStart: Date | null,
    windowEnd: Date | null,
    teamHrsByProject: Map<string, number>,
    projDates: Map<string, { start: Date | null; end: Date | null }>,
    supply: number,
  ) => {
    if (!windowStart || !windowEnd || supply <= 0 || windowEnd < windowStart) {
      return { util: 0, demand: 0, supply, tone: utilTone(0), weeks: 0, activeCount: 0, windowStart, windowEnd };
    }
    const ws = mondayOf(windowStart);
    const we = mondayOf(windowEnd);
    let utilSum = 0;
    let demandSum = 0;
    let weeks = 0;
    const allActive = new Set<string>();
    const cur = new Date(ws);
    while (cur <= we) {
      const weekEnd = new Date(cur);
      weekEnd.setDate(cur.getDate() + 6);
      let demand = 0;
      for (const [pid, hrs] of teamHrsByProject) {
        const d = projDates.get(pid);
        if (!d?.start || !d?.end) continue;
        if (d.start <= weekEnd && d.end >= cur) {
          demand += hrs;
          allActive.add(pid);
        }
      }
      utilSum += demand / supply;
      demandSum += demand;
      weeks++;
      cur.setDate(cur.getDate() + 7);
    }
    const util = weeks > 0 ? utilSum / weeks : 0;
    return {
      util,
      demand: weeks > 0 ? demandSum / weeks : 0,
      supply,
      tone: utilTone(util),
      weeks,
      activeCount: allActive.size,
      windowStart: ws,
      windowEnd: we,
    };
  };

  /** Total team hrs/wk per project from a demand list, OWNED projects only. */
  const buildTeamHrsByProject = (demand: typeof teamDemand, ownedIds: Set<string>) => {
    const m = new Map<string, number>();
    for (const d of demand) {
      if (!d.include_in_capacity) continue;
      for (const p of d.projects ?? []) {
        if (!ownedIds.has(p.project_id)) continue;
        m.set(p.project_id, (m.get(p.project_id) ?? 0) + (p.weekly_hours ?? 0));
      }
    }
    return m;
  };

  const inflightAvg = useMemo(() => {
    const ownedIds = new Set<string>();
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    for (const [pid, meta] of ownedProjectMeta) {
      if (!isInFlightStatus(meta.status)) continue;
      ownedIds.add(pid);
      if (meta.start && (!minStart || meta.start < minStart)) minStart = meta.start;
      if (meta.end && (!maxEnd || meta.end > maxEnd)) maxEnd = meta.end;
    }
    const projDates = new Map<string, { start: Date | null; end: Date | null }>();
    for (const pid of ownedIds) {
      const m = ownedProjectMeta.get(pid)!;
      projDates.set(pid, { start: m.start, end: m.end });
    }
    const teamHrs = buildTeamHrsByProject(currentTeamDemand, ownedIds);
    return computeWindowAvg(minStart, maxEnd, teamHrs, projDates, currentSummary.supply);
  }, [ownedProjectMeta, currentTeamDemand, currentSummary.supply]);

  const forecastAvg = useMemo(() => {
    const queueIds = new Set<string>();
    let minStart: Date | null = null;
    let maxEnd: Date | null = null;
    for (const [pid, meta] of ownedProjectMeta) {
      if (!isApprovedQueueStatus(meta.status)) continue;
      queueIds.add(pid);
      if (meta.start && (!minStart || meta.start < minStart)) minStart = meta.start;
      if (meta.end && (!maxEnd || meta.end > maxEnd)) maxEnd = meta.end;
    }
    // For active-week filtering we want to count BOTH queue projects and
    // any current in-flight project that bleeds into the window. Build a
    // combined dates+hours map.
    const combinedIds = new Set<string>(queueIds);
    for (const [pid, meta] of ownedProjectMeta) {
      if (isInFlightStatus(meta.status)) combinedIds.add(pid);
    }
    const projDates = new Map<string, { start: Date | null; end: Date | null }>();
    for (const pid of combinedIds) {
      const m = ownedProjectMeta.get(pid)!;
      projDates.set(pid, { start: m.start, end: m.end });
    }
    const teamHrs = buildTeamHrsByProject(forecastTeamDemand, combinedIds);
    const result = computeWindowAvg(minStart, maxEnd, teamHrs, projDates, forecastSummary.supply);
    return { ...result, queueCount: queueIds.size };
  }, [ownedProjectMeta, forecastTeamDemand, forecastSummary.supply]);

  const ifTodayAvg = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const queueIds = new Set<string>();
    let maxShiftedEnd: Date | null = null;
    const shiftedDates = new Map<string, { start: Date | null; end: Date | null }>();
    for (const [pid, meta] of ownedProjectMeta) {
      if (!isApprovedQueueStatus(meta.status)) continue;
      if (meta.durationDays == null) continue;
      queueIds.add(pid);
      const start = new Date(today);
      const end = new Date(today);
      end.setDate(end.getDate() + meta.durationDays);
      shiftedDates.set(pid, { start, end });
      if (!maxShiftedEnd || end > maxShiftedEnd) maxShiftedEnd = end;
    }
    // Layer current in-flight projects on their actual dates so the
    // overlap during the queue's run is honestly counted.
    const combinedIds = new Set<string>(queueIds);
    for (const [pid, meta] of ownedProjectMeta) {
      if (!isInFlightStatus(meta.status)) continue;
      combinedIds.add(pid);
      shiftedDates.set(pid, { start: meta.start, end: meta.end });
    }
    const teamHrs = buildTeamHrsByProject(forecastTeamDemand, combinedIds);
    const result = computeWindowAvg(today, maxShiftedEnd, teamHrs, shiftedDates, forecastSummary.supply);
    return { ...result, queueCount: queueIds.size };
  }, [ownedProjectMeta, forecastTeamDemand, forecastSummary.supply]);

  const fmtMD = (d: Date | null) =>
    d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

  // ---- Forward-looking windowed averages (single-team pages) ----------
  // These three replace the in-flight / forecast / if-today triad on the
  // Apps Dev and Synnergie pages. The old triad averaged across the full
  // span of every active project (40+ weeks for some teams), which mixed
  // past, present, and far-future weeks into one scalar that didn't drive
  // any real decision. The new lenses are all forward-looking:
  //   THIS WEEK    — what's on the team right now
  //   NEXT 4 WEEKS — operational ("what's about to break?")
  //   NEXT 12 WEEKS — strategic ("can we absorb a new project?")
  //
  // "In-flight" definition (locked in with Brett):
  //   status ∈ {functional_spec, technical_spec, in_progress, testing_go_live}
  // Approved / ready_for_development is NOT in-flight. It's queue.
  //
  // This Week  → in-flight only (status says we're actually working on it)
  // Next 4/12w → in-flight + queued (planning ahead is the point of the
  //              longer windows; a queue project starting in 6 weeks
  //              should show up in the 12-week view)
  const fwdLookingAvgs = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekStart = mondayOf(today);

    const addDays = (d: Date, n: number) => {
      const r = new Date(d);
      r.setDate(r.getDate() + n);
      return r;
    };

    const inflightIds = new Set<string>();
    const pooledIds = new Set<string>();
    const projDates = new Map<string, { start: Date | null; end: Date | null }>();
    for (const [pid, meta] of ownedProjectMeta) {
      if (isInFlightStatus(meta.status)) {
        inflightIds.add(pid);
        pooledIds.add(pid);
        projDates.set(pid, { start: meta.start, end: meta.end });
      } else if (isApprovedQueueStatus(meta.status)) {
        pooledIds.add(pid);
        projDates.set(pid, { start: meta.start, end: meta.end });
      }
    }
    const teamHrsInflight = buildTeamHrsByProject(forecastTeamDemand, inflightIds);
    const teamHrsPooled = buildTeamHrsByProject(forecastTeamDemand, pooledIds);
    const supply = forecastSummary.supply;

    return {
      thisWeek: computeWindowAvg(weekStart, addDays(weekStart, 6), teamHrsInflight, projDates, supply),
      next4: computeWindowAvg(weekStart, addDays(weekStart, 27), teamHrsPooled, projDates, supply),
      next12: computeWindowAvg(weekStart, addDays(weekStart, 83), teamHrsPooled, projDates, supply),
    };
  }, [ownedProjectMeta, forecastTeamDemand, forecastSummary.supply]);

  // Approved projects whose planned start_date has already passed. The
  // status says "spec hasn't started yet" but the calendar says it
  // should have — surface as a hygiene flag so the team either moves
  // it into functional_spec or pushes the date out.
  //
  // Deliberately scoped to status='approved' ONLY, not the broader
  // queue helper. ready_for_development is intentionally excluded:
  // its start_date points to when spec began (the real start of the
  // project, per Brett's model), so a past date is normal — spec
  // already happened, the project is queued for a developer to pick
  // up. ETE-169 and ETE-100 sit in that state and shouldn't be flagged.
  const stalePlannedStart = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const out: Array<{ id: string; name: string; start: string }> = [];
    for (const p of ownedProjects) {
      if ((p.status ?? "").toLowerCase() !== "approved") continue;
      if (!p.start_date) continue;
      const s = new Date(p.start_date);
      s.setHours(0, 0, 0, 0);
      if (s < today) out.push({ id: p.id, name: p.name, start: p.start_date });
    }
    return out;
  }, [ownedProjects]);

  // Owned in-flight projects with zero hours flowing from any team member.
  // Surfaces the "owned on paper but nobody from our team is on it" gap
  // (e.g. ETE-48 owned by Systems Applications but staffed entirely by
  // PMO + BAs + Infrastructure).
  const unstaffedOwned = useMemo(() => {
    const inflightOwned = new Map<string, string>();
    for (const [pid, meta] of ownedProjectMeta) {
      if (!isInFlightStatus(meta.status)) continue;
      const proj = ownedProjects.find((p) => p.id === pid);
      inflightOwned.set(pid, proj?.name ?? pid);
    }
    const staffed = new Set<string>();
    for (const d of currentTeamDemand) {
      if (!d.include_in_capacity) continue;
      for (const p of d.projects ?? []) {
        if (inflightOwned.has(p.project_id) && (p.weekly_hours ?? 0) > 0) {
          staffed.add(p.project_id);
        }
      }
    }
    const gaps: Array<{ id: string; name: string }> = [];
    for (const [pid, name] of inflightOwned) {
      if (!staffed.has(pid)) gaps.push({ id: pid, name });
    }
    return gaps;
  }, [ownedProjectMeta, currentTeamDemand, ownedProjects]);

  // ---- Per-team breakdown (combined-page only) -----------------------
  // For "All Teams" the page pools both teams. The CFO's aha moment is
  // the imbalance — org may sit at 80% but Synnergie at 130% and Apps
  // Dev at 50%. Each entry below is computed exactly the same way as
  // the page-level inflightAvg / forecastAvg, just scoped to one team.
  const perTeamBreakdown = useMemo(() => {
    if (!team.breakdown) return [];
    const allRoster = Array.isArray(rosterQ.data) ? rosterQ.data : [];
    const allPortfolio = Array.isArray(portfolioQ.data) ? portfolioQ.data : [];

    return team.breakdown.map((b) => {
      const dataKeyLc = b.dataKey.toLowerCase();

      // Members for this single team.
      const members = allRoster.filter((m) => (m.team ?? "").toLowerCase() === dataKeyLc);
      const memberNames = new Set(members.map((m) => m.name));
      const counted = members.filter((m) => m.include_in_capacity);
      const supply = counted.reduce(
        (s, m) => s + (m.weekly_hrs_available ?? 0) * (1 - (m.support_reserve_pct ?? 0)),
        0,
      );

      // Owned projects for this single team — subset of ownedProjectMeta.
      const teamProjMeta = new Map<
        string,
        { status: string | null; start: Date | null; end: Date | null }
      >();
      for (const p of allPortfolio) {
        if ((p.team ?? "").toLowerCase() !== dataKeyLc) continue;
        const status = (p.status ?? "").toLowerCase();
        // Same commitment-only scope as ownedProjects above:
        // in-flight + approved/ready_for_development. Requested,
        // complete, declined, postponed are all excluded.
        const inScope =
          status === "functional_spec" ||
          status === "technical_spec" ||
          status === "in_progress" ||
          status === "testing_go_live" ||
          status === "approved" ||
          status === "ready_for_development";
        if (!inScope) continue;
        teamProjMeta.set(p.id, {
          status: p.status,
          start: p.start_date ? new Date(p.start_date) : null,
          end: p.end_date ? new Date(p.end_date) : null,
        });
      }

      // In-flight window + util.
      let ifMinStart: Date | null = null;
      let ifMaxEnd: Date | null = null;
      const ifIds = new Set<string>();
      for (const [pid, meta] of teamProjMeta) {
        if (!isInFlightStatus(meta.status)) continue;
        ifIds.add(pid);
        if (meta.start && (!ifMinStart || meta.start < ifMinStart)) ifMinStart = meta.start;
        if (meta.end && (!ifMaxEnd || meta.end > ifMaxEnd)) ifMaxEnd = meta.end;
      }
      const ifProjDates = new Map<string, { start: Date | null; end: Date | null }>();
      for (const pid of ifIds) {
        const m = teamProjMeta.get(pid)!;
        ifProjDates.set(pid, { start: m.start, end: m.end });
      }
      const ifMembers = currentTeamDemand.filter((d) => memberNames.has(d.name));
      const ifHrs = buildTeamHrsByProject(ifMembers, ifIds);
      const inflight = computeWindowAvg(ifMinStart, ifMaxEnd, ifHrs, ifProjDates, supply);

      // Forecast window + util — queue projects + any in-flight bleeding in.
      let fcMinStart: Date | null = null;
      let fcMaxEnd: Date | null = null;
      const queueIds = new Set<string>();
      for (const [pid, meta] of teamProjMeta) {
        if (!isApprovedQueueStatus(meta.status)) continue;
        queueIds.add(pid);
        if (meta.start && (!fcMinStart || meta.start < fcMinStart)) fcMinStart = meta.start;
        if (meta.end && (!fcMaxEnd || meta.end > fcMaxEnd)) fcMaxEnd = meta.end;
      }
      const combinedIds = new Set<string>(queueIds);
      for (const [pid, meta] of teamProjMeta) {
        if (isInFlightStatus(meta.status)) combinedIds.add(pid);
      }
      const fcProjDates = new Map<string, { start: Date | null; end: Date | null }>();
      for (const pid of combinedIds) {
        const m = teamProjMeta.get(pid)!;
        fcProjDates.set(pid, { start: m.start, end: m.end });
      }
      const fcMembers = forecastTeamDemand.filter((d) => memberNames.has(d.name));
      const fcHrs = buildTeamHrsByProject(fcMembers, combinedIds);
      const forecast = computeWindowAvg(fcMinStart, fcMaxEnd, fcHrs, fcProjDates, supply);

      // Worst pinch — role with highest util in this team's forecast view.
      // Computes a flat sum/supply per role (not the windowed average) since
      // the goal here is "where's the pressure," not "what's the average."
      type PinchRow = { role_key: string; role_label: string; util: number; demand: number; supply: number };
      const roleAgg = new Map<string, PinchRow>();
      for (const d of fcMembers) {
        if (!d.include_in_capacity) continue;
        const cap = d.capacity_hrs ?? 0;
        const dem = d.total_weekly_hrs ?? 0;
        const k = d.role_key;
        const existing = roleAgg.get(k);
        if (existing) {
          existing.demand += dem;
          existing.supply += cap;
        } else {
          roleAgg.set(k, {
            role_key: k,
            role_label: ROLE_LABEL[k] ?? d.role ?? k,
            util: 0,
            demand: dem,
            supply: cap,
          });
        }
      }
      let worstPinch: PinchRow | null = null;
      for (const r of roleAgg.values()) {
        r.util = r.supply > 0 ? r.demand / r.supply : 0;
        if (r.demand > 0.01 && (!worstPinch || r.util > worstPinch.util)) worstPinch = r;
      }

      return {
        slug: b.slug,
        displayName: b.displayName,
        memberCount: counted.length,
        supply,
        inflight,
        forecast,
        worstPinch,
      };
    });
  }, [team.breakdown, rosterQ.data, portfolioQ.data, currentTeamDemand, forecastTeamDemand]);

  // Gantt-only: same projects but only the ones with both start AND end
  // dates set (the Gantt drops anything missing dates anyway). Also
  // month-aligned Date range for clean column generation. Mirrors the
  // logic Timeline.tsx uses so the look + behavior matches.
  const ganttProjects = useMemo(
    () => ownedProjects.filter((p) => p.start_date && p.end_date),
    [ownedProjects],
  );

  const { ganttRangeStart, ganttRangeEnd } = useMemo(() => {
    // Match the Timeline page: rangeStart = January 1 of the current
    // year (calendar-year anchor). The Gantt component auto-scrolls to
    // today on mount, so prior months are scrollable but never the
    // default landing view. Without this, each team's Gantt opens at a
    // different start (the earliest project's month), making the three
    // team pages look inconsistent.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today.getFullYear(), 0, 1);

    // Forward horizon: minimum end of next year, extended to fit project
    // end-dates up to a hard cap of today + 24 months.
    const minHorizon = new Date(today.getFullYear() + 1, 11, 31);
    const maxHorizon = new Date(today.getFullYear() + 2, 11, 31);

    let end = minHorizon;
    for (const p of ganttProjects) {
      const e = p.end_date ? new Date(p.end_date) : null;
      if (!e) continue;
      if (e > end && e < maxHorizon) {
        end = new Date(e.getFullYear(), e.getMonth() + 1, 1);
      }
    }
    return { ganttRangeStart: start, ganttRangeEnd: end };
  }, [ganttProjects]);

  // ---- Math breakdown — per-project contribution to this team's load.
  // Aggregates teamDemand by project: sum the weekly_hours across all
  // counted team members for each project, also breaking down per role
  // (so the user can see e.g. "ETE-124 contributes 15.2h functional +
  // 22.8h technical to Synnergie"). Cross-references portfolio metadata
  // for status / est_hours / dates so the math stays anchored to the
  // source. Reflects whatever the Forecast toggle is set to since the
  // teamDemand source already filters by include_approved.
  const allRoleKeys = useMemo(() => {
    const set = new Set<string>();
    for (const d of teamDemand) {
      if (!d.include_in_capacity) continue;
      for (const p of d.projects ?? []) {
        if ((p.weekly_hours ?? 0) > 0.001) set.add(p.role_key);
      }
    }
    return Array.from(set).sort();
  }, [teamDemand]);

  const mathRows = useMemo(() => {
    const portMap = new Map<string, { name: string; status: string | null; est_hours: number; start_date: string | null; end_date: string | null; pct_complete: number }>();
    for (const p of (Array.isArray(portfolioQ.data) ? portfolioQ.data : [])) {
      portMap.set(p.id, {
        name: p.name ?? "",
        status: p.status,
        est_hours: p.est_hours ?? 0,
        start_date: p.start_date,
        end_date: p.end_date,
        pct_complete: p.pct_complete ?? 0,
      });
    }
    type Row = {
      project_id: string;
      name: string;
      status: string | null;
      est_hours: number;
      duration_weeks: number | null;
      role_hours: Record<string, number>;
      total: number;
      sources: Set<string>;
    };
    const rows = new Map<string, Row>();
    for (const d of teamDemand) {
      if (!d.include_in_capacity) continue;
      for (const p of d.projects ?? []) {
        const hrs = p.weekly_hours ?? 0;
        if (hrs <= 0.001) continue;
        let row = rows.get(p.project_id);
        if (!row) {
          const meta = portMap.get(p.project_id);
          let dur: number | null = null;
          if (meta?.start_date && meta?.end_date) {
            const ms = new Date(meta.end_date).getTime() - new Date(meta.start_date).getTime();
            dur = ms > 0 ? Math.round(ms / (7 * 86400000)) : null;
          }
          row = {
            project_id: p.project_id,
            name: meta?.name ?? p.project_name,
            status: meta?.status ?? null,
            est_hours: meta?.est_hours ?? 0,
            duration_weeks: dur,
            role_hours: {},
            total: 0,
            sources: new Set(),
          };
          rows.set(p.project_id, row);
        }
        row.role_hours[p.role_key] = (row.role_hours[p.role_key] ?? 0) + hrs;
        row.total += hrs;
        row.sources.add(p.source);
      }
    }
    return Array.from(rows.values()).sort((a, b) => b.total - a.total);
  }, [teamDemand, portfolioQ.data]);

  const mathTotal = useMemo(
    () => mathRows.reduce((s, r) => s + r.total, 0),
    [mathRows],
  );

  // Aggregate per-role load — primary unit on the People section. At
  // planning time you don't pick a person, you pick a role: "do we have
  // a BA?" Roles aggregate the team's individuals; expand to drill in.
  type RoleGroup = {
    role_key: string;
    role_label: string;
    members: typeof teamDemand;
    total_demand: number;          // includes assigned + even_split
    assigned_demand: number;       // explicit-assignment hours only
    unassigned_demand: number;     // even_split (fallback) hours only
    total_supply: number;
    util: number;                  // total / supply (full picture)
    assigned_util: number;         // assigned / supply (committed only)
    tone: ReturnType<typeof utilTone>;
    /** Project-level slices that came from the fallback (no assignment).
     *  Aggregated across people — each project appears once with its
     *  total fallback hours for the role. Drives the "Unassigned demand"
     *  list below the per-person rows. */
    unassigned_projects: Array<{ project_id: string; project_name: string; weekly_hours: number }>;
  };
  const roleGroups = useMemo<RoleGroup[]>(() => {
    const map = new Map<string, RoleGroup>();
    for (const d of teamDemand) {
      const key = d.role_key;
      if (!map.has(key)) {
        map.set(key, {
          role_key: key,
          role_label: ROLE_LABEL[key] ?? d.role ?? key,
          members: [],
          total_demand: 0,
          assigned_demand: 0,
          unassigned_demand: 0,
          total_supply: 0,
          util: 0,
          assigned_util: 0,
          tone: "slate",
          unassigned_projects: [],
        });
      }
      const g = map.get(key)!;
      g.members.push(d);
      // Excluded members (include_in_capacity=False) appear in the
      // expanded individual list but are excluded from the aggregate
      // math — they don't generate counted demand or supply.
      if (!d.include_in_capacity) continue;
      g.total_supply += d.capacity_hrs ?? 0;
      // Per-project bucketing: split each person's project entries by
      // source. Even-split entries from the same project are summed
      // into a single role-level "unassigned" line (since they all
      // came from one project's fallback distribution).
      const fallbackByProject = new Map<string, { project_name: string; hrs: number }>();
      for (const p of d.projects ?? []) {
        const hrs = p.weekly_hours ?? 0;
        g.total_demand += hrs;
        if (p.source === "assigned") {
          g.assigned_demand += hrs;
        } else {
          g.unassigned_demand += hrs;
          // Aggregate fallback hours per project across all members.
          const existing = fallbackByProject.get(p.project_id);
          if (existing) {
            existing.hrs += hrs;
          } else {
            fallbackByProject.set(p.project_id, { project_name: p.project_name, hrs });
          }
        }
      }
      for (const [pid, info] of fallbackByProject) {
        const existing = g.unassigned_projects.find((up) => up.project_id === pid);
        if (existing) {
          existing.weekly_hours += info.hrs;
        } else {
          g.unassigned_projects.push({ project_id: pid, project_name: info.project_name, weekly_hours: info.hrs });
        }
      }
    }
    for (const g of map.values()) {
      g.util = g.total_supply > 0 ? g.total_demand / g.total_supply : 0;
      g.assigned_util = g.total_supply > 0 ? g.assigned_demand / g.total_supply : 0;
      g.tone = utilTone(g.util);
      g.unassigned_projects.sort((a, b) => b.weekly_hours - a.weekly_hours);
    }
    // Sort: red → amber → green → slate, then by demand desc within tone.
    const toneOrder: Record<string, number> = { red: 0, amber: 1, green: 2, slate: 3 };
    return Array.from(map.values()).sort((a, b) => {
      const t = (toneOrder[a.tone] ?? 9) - (toneOrder[b.tone] ?? 9);
      if (t !== 0) return t;
      return b.total_demand - a.total_demand;
    });
  }, [teamDemand]);

  // Track which role rows are expanded to show individuals.
  const [expandedRoles, setExpandedRoles] = useState<Set<string>>(new Set());
  const toggleRole = (key: string) => {
    setExpandedRoles((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Idle roles (zero demand) are noise when scanning for pinch points,
  // but they ARE useful — "we have unused BA capacity" is a planning
  // signal too. Hide them by default, surface behind a toggle.
  const [showIdle, setShowIdle] = useState(false);
  const activeRoles = useMemo(
    () => roleGroups.filter((g) => g.total_demand > 0.01),
    [roleGroups],
  );
  const idleRoles = useMemo(
    () => roleGroups.filter((g) => g.total_demand <= 0.01),
    [roleGroups],
  );
  const visibleRoles = showIdle ? roleGroups : activeRoles;

  const isLoading = rosterQ.isLoading || demandQ.isLoading || portfolioQ.isLoading;

  return (
    <>
      <TopBar
        title={`Team · ${TEAM_NAME}`}
        subtitle="Forecast view — includes current + approved/queued work. Status pills mark which is which."
      />

      <div className="space-y-6 p-8">
        {isLoading && (
          <div className="rounded-xl border border-line bg-surface p-12 text-center text-sm text-ink-muted">
            Loading team view...
          </div>
        )}

        {!isLoading && (
          <>

            {/* ---- Per-team breakdown (combined view only) ---- */}
            {/* The "aha" element for the executive audience: the org-wide
                3-up above is one number per metric, but the imbalance lives
                per-team. A team card here shows that team's own in-flight +
                forecast util plus the worst-pinch role, so the user sees at
                a glance "Synnergie Functional is the choke point" without
                clicking through. */}
            {isCombined && perTeamBreakdown.length > 0 && (
              <div className="rounded-xl border border-line bg-surface p-5">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-ink-subtle" />
                  <h2 className="text-sm font-semibold text-ink-2">Per-team breakdown</h2>
                  <span className="ml-auto text-[10px] text-ink-subtle">
                    Click a card to drill into that team's view
                  </span>
                </div>
                <div className={cn(
                  "mt-3 grid gap-3",
                  perTeamBreakdown.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3",
                )}>
                  {perTeamBreakdown.map((t) => {
                    const ifCls = TONE_CLASSES[t.inflight.tone];
                    const fcCls = TONE_CLASSES[t.forecast.tone];
                    const pinchCls = t.worstPinch ? TONE_CLASSES[utilTone(t.worstPinch.util)] : TONE_CLASSES.slate;
                    return (
                      <Link
                        key={t.slug}
                        to={`/teams/${t.slug}`}
                        className="block rounded-lg border border-line bg-canvas/60 p-4 transition-colors hover:border-brand-navy-300 hover:bg-brand-navy-50/30 dark:hover:bg-brand-navy-500/10"
                      >
                        <div className="flex items-baseline justify-between">
                          <span className="text-sm font-semibold text-ink">{t.displayName}</span>
                          <span className="text-[10px] tabular-nums text-ink-muted">
                            {t.memberCount} counted · {Math.round(t.supply)}h/wk
                          </span>
                        </div>
                        <div className="mt-3 grid grid-cols-2 gap-3">
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-ink-subtle">In-flight</div>
                            <div className={cn("mt-0.5 text-2xl font-extrabold tabular-nums", ifCls.text)}>
                              {t.inflight.weeks > 0 ? `${Math.round(t.inflight.util * 100)}%` : "—"}
                            </div>
                            <div className="text-[10px] tabular-nums text-ink-muted">
                              {t.inflight.weeks > 0
                                ? `${Math.round(t.inflight.demand)}h avg · ${t.inflight.activeCount} proj`
                                : "no current work"}
                            </div>
                          </div>
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-ink-subtle">Forecast</div>
                            <div className={cn("mt-0.5 text-2xl font-extrabold tabular-nums", fcCls.text)}>
                              {t.forecast.weeks > 0 ? `${Math.round(t.forecast.util * 100)}%` : "—"}
                            </div>
                            <div className="text-[10px] tabular-nums text-ink-muted">
                              {t.forecast.weeks > 0
                                ? `${Math.round(t.forecast.demand)}h avg · ${t.forecast.activeCount} proj`
                                : "nothing in queue"}
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 border-t border-line pt-2">
                          <div className="flex items-center justify-between text-[10px]">
                            <span className="font-semibold uppercase tracking-wider text-ink-muted">
                              Worst pinch
                            </span>
                            {t.worstPinch ? (
                              <span className="flex items-baseline gap-1.5">
                                <span className="font-medium text-ink-2">{t.worstPinch.role_label}</span>
                                <span className={cn("font-bold tabular-nums", pinchCls.text)}>
                                  {Math.round(t.worstPinch.util * 100)}%
                                </span>
                              </span>
                            ) : (
                              <span className="italic text-ink-subtle">none</span>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ---- Projects We Own — table with mini bars ---- */}
            <div className="rounded-xl border border-line bg-surface">
              <div className="flex items-center justify-between border-b border-line-soft px-5 py-3">
                <div className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-ink-subtle" />
                  <h2 className="text-sm font-semibold text-ink-2">Projects We Own</h2>
                  <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-semibold tabular-nums text-ink-3">
                    {ownedProjects.length}
                  </span>
                </div>
                <div className="text-[10px] text-ink-subtle">
                  Bars colored by health · sorted active → approved
                </div>
              </div>

              {ownedProjects.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-ink-subtle">
                  No active or approved projects assigned to {TEAM_NAME}.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-canvas text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">ID</th>
                        <th className="px-4 py-2 text-left">Project</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-left">Health</th>
                        <th className="px-4 py-2 text-right">Progress</th>
                        <th className="px-4 py-2 text-left">Timeline</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-soft">
                      {ownedProjects.map((p) => {
                        const pct = Math.round((p.pct_complete ?? 0) * 100);
                        return (
                          <tr key={p.id} className="hover:bg-raised">
                            <td className="px-4 py-2.5 font-mono text-[10px] text-ink-muted">
                              <Link to={`/portfolio/${p.id}`} className="hover:text-brand-navy-700 dark:hover:text-brand-navy-300">{p.id}</Link>
                            </td>
                            <td className="px-4 py-2.5 font-medium text-ink">
                              <Link to={`/portfolio/${p.id}`} className="hover:text-brand-navy-700 dark:hover:text-brand-navy-300">
                                {p.name}
                              </Link>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", statusPillClass(p.status))}>
                                {statusShortLabel(p.status)}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              {(() => {
                                const eh = effectiveHealth(p.status, p.health);
                                return (
                                  <span className={cn("inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold", eh.pillClass)}>
                                    {eh.label}
                                  </span>
                                );
                              })()}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-ink-3">
                              {pct}%
                            </td>
                            <td className="px-4 py-2.5 text-[10px] tabular-nums text-ink-muted">
                              {p.start_date && p.end_date
                                ? `${formatDate(p.start_date)} → ${formatDate(p.end_date)}`
                                : "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* ---- Full Gantt for owned projects ---- */}
            {/* Sits right under the Projects We Own table — the same projects,
                now on a calendar. Same component the Timeline page uses,
                scoped to this team's owned projects, with health-colored bars
                + milestone diamonds. Skips projects missing start or end
                dates (the Gantt requires both). */}
            {ganttProjects.length > 0 && (
              <GanttChart
                projects={ganttProjects}
                rangeStart={ganttRangeStart}
                rangeEnd={ganttRangeEnd}
                zoom="weeks"
                milestones={milestonesQ.data ?? []}
                showMilestones={true}
              />
            )}

            {/* Week-by-week team heatmaps retired 2026-09-11 with the
                flat-hrs/week capacity model — /capacity's people ledger
                is the load view now. */}

            {/* ---- Team Roster — verification-grade per-person view ---- */}
            {/* Source-of-truth table: every member's raw weekly hours, the
                support reserve carved out, and the resulting available
                hours/week the engine actually uses. Totals at the bottom
                let the user reconcile the headline supply against
                weekly_hrs_available × (1 − support_reserve_pct). */}
            <div className="rounded-xl border border-line bg-surface">
              <div className="flex items-center gap-2 border-b border-line-soft px-5 py-3">
                <Users className="h-4 w-4 text-ink-subtle" />
                <h2 className="text-sm font-semibold text-ink-2">Team Roster</h2>
                <span className="rounded-full bg-raised px-2 py-0.5 text-[10px] font-semibold tabular-nums text-ink-3">
                  {teamMembers.length} {teamMembers.length === 1 ? "person" : "people"}
                </span>
                <span className="ml-auto text-[10px] text-ink-subtle">
                  Click a person to see their projects &amp; when they free up
                </span>
              </div>
              {teamMembers.length === 0 ? (
                <div className="px-5 py-12 text-center text-sm text-ink-subtle">
                  No people on {TEAM_NAME}.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-canvas text-[10px] font-semibold uppercase tracking-wider text-ink-muted">
                      <tr>
                        <th className="px-4 py-2 text-left">Person</th>
                        <th className="px-4 py-2 text-left">Allocation</th>
                        <th className="px-4 py-2 text-right">Free now</th>
                        <th className="px-4 py-2 text-left">Free from</th>
                        <th className="px-4 py-2 text-right">Capacity</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-line-soft">
                      {teamMembers
                        .slice()
                        .sort((a, b) => {
                          // Counted first; within counted, MOST FREE first
                          // (least allocated → top — "who can take the next
                          // project?"). Excluded last, by name.
                          const ai = a.include_in_capacity ? 0 : 1;
                          const bi = b.include_in_capacity ? 0 : 1;
                          if (ai !== bi) return ai - bi;
                          if (a.include_in_capacity) {
                            const ua = availabilityByPerson.get(a.name)?.utilPct ?? 0;
                            const ub = availabilityByPerson.get(b.name)?.utilPct ?? 0;
                            if (ua !== ub) return ua - ub;
                          }
                          return a.name.localeCompare(b.name);
                        })
                        .map((m) => {
                          const reserve = m.support_reserve_pct ?? 0;
                          const weekly = m.weekly_hrs_available ?? 0;
                          const capacity = weekly * (1 - reserve);
                          const excluded = !m.include_in_capacity;
                          const isOpen = expandedPeople.has(m.name);
                          const projects = (assignmentsByPerson.get(m.name) ?? []).filter((p) => !projPhase(p).ended);
                          const availInfo = availabilityByPerson.get(m.name);
                          const util = availInfo?.utilPct ?? 0;
                          const spare = availInfo?.spareHrs ?? capacity;
                          const barFill = util >= 1 ? "bg-rose-500" : util >= 0.8 ? "bg-amber-500" : util > 0 ? "bg-emerald-500" : "bg-muted";
                          const barText = util >= 1 ? "text-rose-700 dark:text-rose-300" : util >= 0.8 ? "text-amber-700 dark:text-amber-300" : util > 0 ? "text-emerald-700 dark:text-emerald-300" : "text-ink-subtle";
                          return (
                            <Fragment key={m.name}>
                            <tr
                              className={cn("cursor-pointer hover:bg-raised", isOpen && "bg-canvas", excluded && "opacity-60")}
                              onClick={() => togglePerson(m.name)}
                            >
                              {/* Person */}
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-2">
                                  {isOpen
                                    ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />
                                    : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-ink-subtle" />}
                                  <div
                                    className={cn("flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold", excluded && "grayscale")}
                                    style={{ backgroundColor: avatarTone(m.name) }}
                                  >
                                    {initials(m.name)}
                                  </div>
                                  <div className="min-w-0 leading-tight">
                                    <div className={cn("font-medium", excluded ? "text-ink-subtle" : "text-ink")}>{m.name}</div>
                                    <div className="text-[10px] text-ink-subtle">
                                      {ROLE_LABEL[m.role_key] ?? m.role ?? m.role_key}
                                      {isCombined && m.team ? ` · ${m.team}` : ""}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              {/* Allocation */}
                              <td className="px-4 py-2.5">
                                {excluded ? (
                                  <span className="text-ink-faint">—</span>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <div className="h-1.5 w-24 overflow-hidden rounded-full bg-raised">
                                      <div className={cn("h-full rounded-full transition-all", barFill)} style={{ width: `${Math.min(Math.max(util, 0), 1) * 100}%` }} />
                                    </div>
                                    <span className={cn("w-9 text-right text-[11px] font-semibold tabular-nums", barText)}>{Math.round(util * 100)}%</span>
                                  </div>
                                )}
                              </td>
                              {/* Free now */}
                              <td className="px-4 py-2.5 text-right tabular-nums">
                                {excluded ? (
                                  <span className="text-ink-faint">—</span>
                                ) : spare >= 1 ? (
                                  <span className="font-semibold text-emerald-600 dark:text-emerald-400">{Math.round(spare)}h</span>
                                ) : spare <= -1 ? (
                                  <span className="font-semibold text-rose-600 dark:text-rose-400">{Math.round(spare)}h</span>
                                ) : (
                                  <span className="text-ink-subtle">0h</span>
                                )}
                              </td>
                              {/* Free from */}
                              <td className="px-4 py-2.5 text-[11px]">
                                {excluded ? (
                                  <span className="text-ink-faint">—</span>
                                ) : projects.length === 0 ? (
                                  <span className="font-medium text-emerald-600 dark:text-emerald-400">Now</span>
                                ) : availInfo?.hasOpenEnded ? (
                                  <span className="text-ink-subtle">open-ended</span>
                                ) : availInfo?.clears ? (
                                  <span className="tabular-nums text-ink-3">{formatDate(availInfo.clears)}</span>
                                ) : (
                                  <span className="font-medium text-emerald-600 dark:text-emerald-400">Now</span>
                                )}
                              </td>
                              {/* Capacity (after support reserve) */}
                              <td className="px-4 py-2.5 text-right tabular-nums">
                                <span
                                  className={cn(excluded ? "text-ink-subtle line-through" : "text-ink-2")}
                                  title={`${Math.round(weekly)}h weekly${reserve > 0 ? ` × (1 − ${Math.round(reserve * 100)}% support reserve)` : ""} = ${Math.round(capacity)}h for projects`}
                                >
                                  {Math.round(capacity)}h
                                </span>
                              </td>
                              {/* Status */}
                              <td className="px-4 py-2.5">
                                {excluded ? (
                                  <span className="rounded-full bg-raised px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-ink-subtle" title="Support-only — excluded from project capacity">
                                    excluded
                                  </span>
                                ) : (
                                  <span className="rounded-full bg-emerald-50 dark:bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                                    counted
                                  </span>
                                )}
                              </td>
                            </tr>
                            {isOpen && projects.length > 0 && (
                              <tr className="bg-canvas/60">
                                <td colSpan={6} className="px-4 pb-3 pt-0">
                                  <div className="ml-9 overflow-hidden rounded-lg border border-line bg-surface">
                                    <table className="w-full text-[11px]">
                                      <thead className="bg-canvas text-[9px] uppercase tracking-wider text-ink-subtle">
                                        <tr>
                                          <th className="px-3 py-1.5 text-left font-semibold">Assigned project</th>
                                          <th className="px-3 py-1.5 text-left font-semibold">Start</th>
                                          <th className="px-3 py-1.5 text-left font-semibold">End</th>
                                          <th className="px-3 py-1.5 text-right font-semibold">h/wk</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-line-soft">
                                        {projects.map((p, i) => (
                                          <tr key={p.id} className={cn(i === projects.length - 1 && "bg-emerald-50/40 dark:bg-emerald-500/10")}>
                                            <td className="px-3 py-1.5">
                                              <Link
                                                to={`/portfolio/${p.id}`}
                                                className="hover:text-indigo-600 dark:hover:text-indigo-400"
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <span className="font-mono text-[10px] text-ink-subtle">{p.id}</span>{" "}
                                                <span className="font-medium text-ink-2">{p.name}</span>
                                              </Link>
                                              {projPhase(p).upcoming && (
                                                <span
                                                  className="ml-1.5 rounded-full bg-raised px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-ink-muted"
                                                  title="Starts in the future — not consuming capacity yet"
                                                >
                                                  upcoming
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-1.5 tabular-nums text-ink-muted">{formatDate(p.start)}</td>
                                            <td className="px-3 py-1.5 tabular-nums text-ink-2">
                                              {p.end ? formatDate(p.end) : <span className="text-ink-faint">open</span>}
                                            </td>
                                            <td className="px-3 py-1.5 text-right tabular-nums text-ink-3">{Math.round(p.hours)}h</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                    {availInfo && (
                                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-0.5 border-t border-line-soft bg-canvas px-3 py-1.5 text-[10px] text-ink-muted">
                                        <span>Project capacity <span className="font-semibold text-ink-2">{Math.round(availInfo.capacity)}h/wk</span></span>
                                        <span>Assigned <span className="font-semibold text-ink-2">{Math.round(availInfo.assignedHrs)}h/wk</span></span>
                                        <span>Free now <span className={cn("font-semibold", availInfo.spareHrs >= 1 ? "text-emerald-600 dark:text-emerald-400" : "text-ink-2")}>{Math.round(availInfo.spareHrs)}h/wk</span></span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                            {isOpen && projects.length === 0 && (
                              <tr className="bg-canvas/60">
                                <td colSpan={6} className="px-4 pb-3 pt-0">
                                  <div className="ml-9 rounded-lg border border-dashed border-line-strong bg-surface px-3 py-2.5 text-[11px] text-ink-muted">
                                    No current or upcoming project assignments — {m.name.split(" ")[0]} is free{" "}
                                    {excluded ? "(not counted toward capacity)" : `for ${Math.round(spare)}h/wk`}. Assign them on{" "}
                                    <Link
                                      to="/capacity"
                                      className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      Capacity
                                    </Link>
                                    .
                                  </div>
                                </td>
                              </tr>
                            )}
                            </Fragment>
                          );
                        })}
                      {/* Totals — counted only (excluded folks line-through above). */}
                      {(() => {
                        const counted = teamMembers.filter((m) => m.include_in_capacity);
                        const totalWeekly = counted.reduce((s, m) => s + (m.weekly_hrs_available ?? 0), 0);
                        const totalCap = counted.reduce(
                          (s, m) => s + (m.weekly_hrs_available ?? 0) * (1 - (m.support_reserve_pct ?? 0)),
                          0,
                        );
                        const totalAssigned = counted.reduce(
                          (s, m) => s + (availabilityByPerson.get(m.name)?.assignedHrs ?? 0),
                          0,
                        );
                        const totalSpare = totalCap - totalAssigned;
                        const teamUtil = totalCap > 0 ? totalAssigned / totalCap : 0;
                        const excludedCount = teamMembers.length - counted.length;
                        return (
                          <tr className="border-t-2 border-line bg-canvas font-semibold">
                            <td className="px-4 py-2.5 text-[10px] uppercase tracking-wider text-ink-muted">
                              Team — counted ({counted.length})
                              {excludedCount > 0 && (
                                <span className="ml-1 text-ink-subtle normal-case"> · {excludedCount} excluded</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2">
                                <div className="h-1.5 w-24 overflow-hidden rounded-full bg-muted">
                                  <div
                                    className={cn("h-full rounded-full", teamUtil >= 1 ? "bg-rose-500" : teamUtil >= 0.8 ? "bg-amber-500" : "bg-emerald-500")}
                                    style={{ width: `${Math.min(Math.max(teamUtil, 0), 1) * 100}%` }}
                                  />
                                </div>
                                <span className="w-9 text-right text-[11px] tabular-nums text-ink-2">{Math.round(teamUtil * 100)}%</span>
                              </div>
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-emerald-700 dark:text-emerald-300">{Math.round(totalSpare)}h</td>
                            <td className="px-4 py-2.5"></td>
                            <td className="px-4 py-2.5 text-right tabular-nums text-ink" title={`${Math.round(totalWeekly)}h gross before support reserve`}>
                              {Math.round(totalCap)}h
                            </td>
                            <td className="px-4 py-2.5 text-[9px] italic text-ink-subtle">matches headline supply</td>
                          </tr>
                        );
                      })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>


          </>
        )}
      </div>

    </>
  );
}

// ---------------------------------------------------------------------------
// UtilStat — utilization stat with %, verdict pill, demand/supply breakdown.
// ---------------------------------------------------------------------------

function UtilStat({
  label,
  sublabel,
  summary,
}: {
  label: string;
  sublabel: string;
  summary: { supply: number; demand: number; util: number; tone: ReturnType<typeof utilTone> };
}) {
  const cls = TONE_CLASSES[summary.tone];
  return (
    <div className="rounded-lg border border-line bg-canvas/60 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{label}</span>
        <span className={cn("rounded-full px-1.5 py-0.5 text-[9px] font-semibold", cls.bg, cls.text)}>
          {cls.label}
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className={cn("text-3xl font-extrabold tabular-nums", cls.text)}>
          {Math.round(summary.util * 100)}%
        </span>
      </div>
      <div className="mt-1 text-[10px] tabular-nums text-ink-muted">
        <span className="font-semibold text-ink-2">{Math.round(summary.demand)}h</span>
        <span className="text-ink-subtle"> demand · </span>
        <span className="font-semibold text-ink-2">{Math.round(summary.supply)}h</span>
        <span className="text-ink-subtle"> supply</span>
      </div>
      <div className="mt-1 text-[10px] italic text-ink-subtle">{sublabel}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// (Forecast preview cards removed — snapshot model is the single forecast
//  number, rendered inline in UtilStat above.)
// ---------------------------------------------------------------------------

